import { useEffect, useState, useContext } from "react";
import { useCreateFolderMutation } from "../backend/hooks/importExportMenu/mutationCreateFolder";
import {
  extractIdAndPathFromParameter,
  COMMENT_ID_LABEL
} from "../helpers/urlHelper";
import { useHistory } from "react-router-dom";
import { useRepositoryObject } from "../backend/hooks/pmxActivity/queryGetRepositoryObject";
import { useRepositoryObjects } from "../backend/hooks/pmxActivity/queryGetRepositoryObjects";
import { PmxActivityDetailsContext } from "../contexts/pmxActivityDetailsProvider";
import {
  GetRepositoryObjectsInput,
  Repo,
  RepositoryObject
} from "../backend/types";
import { useForceRefreshPmxActivity } from "../backend/hooks/pmxActivity/mutationforceRefreshPmxActivity";

// This hooked is reused in multiple places, and needed for the functions related to browsing the repository
export function useBrowseRepositoryHook() {
  const { activityData } = useContext(PmxActivityDetailsContext);
  const history = useHistory();
  let url = window.location.pathname;
  url = url.replace("/activity/", "");
  const {
    activityId,
    path,
    versionId,
    openProperties = false,
    repoId,
    commentId
  } = extractIdAndPathFromParameter(url);

  let selectedCommentIdFromMenuFromURL = null;
  if (window.location.href.split("#" + COMMENT_ID_LABEL).length > 1) {
    selectedCommentIdFromMenuFromURL = Number(
      window.location.href.split("#" + COMMENT_ID_LABEL)[1]
    );
  }

  if (commentId) {
    selectedCommentIdFromMenuFromURL = Number(commentId);
  }

  // This is not split up, because we need to set the entire thing on a back button, and the hook to only to trigger once
  const [currentSelectedState, setCurrentSelectedState] = useState({
    currentSelectedPath: path ? path : "/",
    currentSelectedVersionId: versionId ? versionId : "",
    currentSelectedRepoId: repoId
      ? repoId
      : activityData?.mainRepository.id.toString(),
    currentSelectedCommentId: commentId ? commentId : null
  });

  const setCurrentSelectedPath = (path: string) => {
    setCurrentSelectedState((prevstate) => ({
      ...prevstate,
      currentSelectedPath: path
    }));
  };
  const setCurrentSelectedVersionId = (versionId: string) => {
    setCurrentSelectedState((prevstate) => ({
      ...prevstate,
      currentSelectedVersionId: versionId
    }));
  };
  const setCurrentSelectedRepoId = (repoId: string) => {
    setCurrentSelectedState((prevstate) => ({
      ...prevstate,
      currentSelectedRepoId: repoId
    }));
  };

  const [currentSelectedRevision, setCurrentSelectedRevision] = useState("");
  const [isCreateFolderWindowOpen, setIsCreateFolderWindowOpen] =
    useState(false);
  const [isActionOfFilesVisible, setIsActionOfFilesVisible] = useState(false);
  const [isEditActionVisible, setIsEditActionVisible] = useState(false);
  const [isTracebilityReportVisible, setIsTracebilityReportVisible] =
    useState(false);
  const [tableMenuWasOpenOnPath, setTableMenuWasOpenOnPath] = useState("");
  const [selectedCommentIdFromMenu, setSelectedCommentIdFromMenu] = useState<
    null | number
  >(selectedCommentIdFromMenuFromURL);
  const [isProcessingExport, setIsProcessingExport] = useState(false);
  const [openPropertiesModal, setOpenPropertiesModal] =
    useState<boolean>(false);
  const [isCopyMoveModalOpenHook, setIsCopyMoveModalOpenHook] = useState(false);
  const [isPreselectedRevision, setIsPreselectedRevision] =
    useState<boolean>(false);

  const {
    dataRefreshPmxActivity,
    forceRefreshPmxActivity,
    isRefreshPmxActivityLoading,
    errorRefreshPmxActivity
  } = useForceRefreshPmxActivity(activityData?.id);

  const {
    callMultipleRepository,
    callRepositoryObjects,
    refetchRepositoryObjects,
    sortAndSetTableView,
    foldersAndCount,
    tableViewFiles,
    treeHierarchy,
    getRepositoryObjectsError,
    isGetRepositoryObjectsLoading
  } = useRepositoryObjects(activityData?.mainRepository?.id);

  const { callSingleRepositoryObject } = useRepositoryObject();

  const getRevisionAndSetCurrentSelectedRevision = (versionId: string) => {
    callSingleRepositoryObject({
      variables: {
        versionId
      }
    }).then(({ data }: any) => {
      const revision = data.getRepositoryObject.repositoryObject.revision;
      setCurrentSelectedRevision(revision);
    });
  };

  // On initial component load
  // Extract repo id and path (if exist)
  useEffect(() => {
    if (activityData) {
      const repoIds = [
        ...(Array.isArray(activityData.readOnlyRepositories)
          ? activityData.readOnlyRepositories
              .filter((repo: Repo | null | undefined): repo is Repo => !!repo)
              .map((repo: Repo) => repo.id)
          : []),
        activityData.mainRepository.id
      ];
      const repositoryObjectsInput: GetRepositoryObjectsInput[] = repoIds.map(
        (repoId: string) => ({
          repoId: String(repoId)
        })
      );
      // if in a nested path, it will retrieve the the top tree level and also the nested path to the location
      callMultipleRepository(repositoryObjectsInput).then(() => {
        const page = window.location.href.split("/").pop();
        const { path, repoId } = extractIdAndPathFromParameter(page!);

        const handlePathParsing = () => {
          const parseFilePathList = path.split("/").slice(0, -1);
          let pathAccumulator = "";
          const repoIdStr = repoId
            ? repoId.toString()
            : activityData.mainRepository.id.toString();
          const locations = parseFilePathList.map((segment) => {
            pathAccumulator += segment + "/";
            return pathAccumulator;
          });
          Promise.all(
            locations.map((location) =>
              callRepositoryObjects({
                variables: {
                  repoId: repoIdStr,
                  location
                }
              })
            )
          ).then((responses) => {
            const lastResponse = responses[responses.length - 1];
            const tableObjects: RepositoryObject[] = lastResponse.data
              .getRepositoryObjects.repositoryObjects
              ? [...lastResponse.data.getRepositoryObjects.repositoryObjects]
              : [];
            sortAndSetTableView(tableObjects);
          });
        };

        if (path.endsWith("/") && path !== "/") {
          handlePathParsing();
        } else if (path.split("/").length > 1) {
          handlePathParsing();
        } else if (
          !path &&
          repoId &&
          repoId !== activityData.mainRepository.id.toString()
        ) {
          callRepositoryObjects({
            variables: {
              repoId: repoId!.toString(),
              location: "/"
            }
          });
        }
      });
      setCurrentSelectedRepoId(
        repoId ? repoId : activityData.mainRepository.id.toString()
      );
    }

    // set up listener for back button in browser to update the page based on URL, only for this activity page
    const unlisten = history.listen((currentLocation) => {
      if (history.action === "POP") {
        if (window.location.href.includes("/activity/" + activityId)) {
          const page = window.location.href.split("/").pop();
          const { path, versionId, repoId, commentId } =
            extractIdAndPathFromParameter(page!);
          const getPath = path !== "" ? path : "/";
          const getVersionId = versionId ? versionId : "";
          const getRepoId = (): string => {
            if (repoId) {
              return repoId;
            }
            if (currentSelectedState.currentSelectedRepoId) {
              return currentSelectedState.currentSelectedRepoId;
            } else {
              return activityData?.mainRepository.id.toString();
            }
          };

          setCurrentSelectedState({
            currentSelectedPath: getPath,
            currentSelectedVersionId: getVersionId,
            currentSelectedRepoId: getRepoId(),
            currentSelectedCommentId: commentId ? commentId : null
          });

          if (getRepoId()) {
            callRepositoryObjects({
              variables: {
                repoId: getRepoId(),
                location: getPath
              }
            });
          }
        } else {
          window.location.replace(currentLocation.pathname);
        }
      }
    });

    // Cleanup function to unsubscribe from the listener. This ensured not evey change to the URL gets to re-render the page
    return () => {
      unlisten();
    };
  }, [activityData]);

  useEffect(() => {
    //we change the URL on every file change
    const { versionId } = extractIdAndPathFromParameter(window.location.href);
    const currentVersionId =
      currentSelectedState.currentSelectedVersionId &&
      currentSelectedState.currentSelectedVersionId !== ""
        ? currentSelectedState.currentSelectedVersionId
        : versionId;

    currentVersionId &&
      currentSelectedRevision === "" &&
      getRevisionAndSetCurrentSelectedRevision(currentVersionId);
    let mergedPath = "/activity/" + activityId;

    if (
      currentSelectedState.currentSelectedPath &&
      currentSelectedState.currentSelectedPath !== "/"
    ) {
      mergedPath += encodeURIComponent(
        `/${currentSelectedState.currentSelectedPath}`
      );
    }

    if (
      currentVersionId &&
      !currentSelectedState.currentSelectedPath.endsWith("/")
    ) {
      if (currentVersionId !== currentSelectedState.currentSelectedVersionId) {
        setCurrentSelectedState((prevstate) => ({
          ...prevstate,
          currentSelectedVersionId: currentVersionId as string
        }));
      }

      const encodedVersionId = encodeURIComponent("?versionId=");
      mergedPath = `${mergedPath}${encodedVersionId}${currentVersionId}`;
    }

    if (
      currentSelectedState.currentSelectedRepoId &&
      (currentSelectedState.currentSelectedPath || currentVersionId)
    ) {
      const encodedRepoId = mergedPath.includes("versionId")
        ? encodeURIComponent("&repoId=")
        : encodeURIComponent("?repoId=");
      mergedPath = `${mergedPath}${encodedRepoId}${currentSelectedState.currentSelectedRepoId}`;
    }
    if (
      !window.location.href.endsWith(mergedPath) &&
      !mergedPath.includes(
        window.location.pathname +
          "%3FrepoId%3D" +
          activityData?.mainRepository.id
      ) &&
      (currentSelectedState.currentSelectedPath || currentVersionId)
    ) {
      window.history.pushState(
        {},
        currentSelectedState.currentSelectedPath,
        mergedPath
      );
    }

    if (
      currentSelectedState.currentSelectedPath &&
      !currentSelectedState.currentSelectedPath.endsWith("/") &&
      !isCopyMoveModalOpenHook
    ) {
      setTableMenuWasOpenOnPath(currentSelectedState.currentSelectedPath);
    }
  }, [currentSelectedState]);

  // *** MUTATION FUNCTIONS ***
  const {
    createFolderMutation,
    isLoadingCreateFolderMutation,
    errorCreateFolderMutation
  } = useCreateFolderMutation();

  function createFolder(name: string) {
    setIsCreateFolderWindowOpen(false);
    createFolderMutation({
      variables: {
        name: name.trim(),
        parentFolder: currentSelectedState.currentSelectedPath,
        activityId: activityId
      }
    }).then(() => {
      refetchRepositoryObjects();
    });
  }

  // *** MODALS ***
  useEffect(() => {
    openProperties && setOpenPropertiesModal(true);
  }, []);

  const onClosePropertiesModal = () => {
    setIsActionOfFilesVisible(false);
    setOpenPropertiesModal(false);
    setTableMenuWasOpenOnPath("");
    setIsPreselectedRevision(false);
  };

  const onCloseTracebilityModal = () => {
    setIsTracebilityReportVisible(false);
    setTableMenuWasOpenOnPath("");
  };

  return {
    isCreateFolderWindowOpen,
    setIsCreateFolderWindowOpen,
    isLoadingCreateFolderMutation,
    errorCreateFolderMutation,
    setCurrentSelectedPath,
    setCurrentSelectedVersionId,
    setCurrentSelectedRepoId,
    setCurrentSelectedRevision,
    createFolder,
    currentSelectedPath: currentSelectedState.currentSelectedPath,
    currentSelectedVersionId: currentSelectedState.currentSelectedVersionId,
    currentSelectedRepoId: currentSelectedState.currentSelectedRepoId,
    currentSelectedRevision,
    isActionOfFilesVisible,
    setIsActionOfFilesVisible,
    isEditActionVisible,
    setIsEditActionVisible,
    tableMenuWasOpenOnPath,
    setTableMenuWasOpenOnPath,
    onClosePropertiesModal,
    onCloseTracebilityModal,
    isTracebilityReportVisible,
    setIsTracebilityReportVisible,
    selectedCommentIdFromMenu,
    setSelectedCommentIdFromMenu,
    isProcessingExport,
    setIsProcessingExport,
    openPropertiesModal,
    isCopyMoveModalOpenHook,
    setIsCopyMoveModalOpenHook,
    isPreselectedRevision,
    setIsPreselectedRevision,
    //  New Repository Objects Query
    foldersAndCount,
    tableViewFiles,
    treeHierarchy,
    callRepositoryObjects,
    refetchRepositoryObjects,
    isGetRepositoryObjectsLoading,
    getRepositoryObjectsError,
    dataRefreshPmxActivity,
    forceRefreshPmxActivity,
    isRefreshPmxActivityLoading,
    errorRefreshPmxActivity
  };
}
